import { ObjectTools } from '../../../tools/object-tools';
import { SolSpinner, SolSpinnerSize } from '../../../models/core/sol-spinner';

export interface SolSpinnerSegment {
  readonly id: number;
  readonly dash: string;
  readonly offset: string;
  readonly segmentClass: string;
}

export class SolSpinnerState {
  public static readonly secondMs = 1000;

  public static readonly ringLength = 100;

  private static readonly defaultSize: SolSpinnerSize = '16';

  private static readonly segmentGap = 6;
  private static readonly maxGapDivisor = 3;

  private constructor(
    public readonly spinner: SolSpinner,
    public readonly size: SolSpinnerSize,
    public readonly now: number,
    public readonly hostClass: string,
    public readonly segments: SolSpinnerSegment[],
  ) {}

  public static of(): SolSpinnerState {
    return new SolSpinnerState(SolSpinner.of(), SolSpinnerState.defaultSize, 0, '', [])
      .recalculateHostClass()
      .recalculateSegments();
  }

  public withSpinner(spinner: SolSpinner, now: number): SolSpinnerState {
    return this.copy({ spinner, now }).recalculateSegments();
  }

  public withSize(size: SolSpinnerSize | undefined): SolSpinnerState {
    return this.copy({ size: size ?? SolSpinnerState.defaultSize }).recalculateHostClass();
  }

  public withNow(now: number): SolSpinnerState {
    return this.copy({ now }).recalculateSegments();
  }

  private recalculateHostClass(): SolSpinnerState {
    return this.copy({ hostClass: `sol-spinner sol-spinner-${this.size}` });
  }

  private recalculateSegments(): SolSpinnerState {
    const countdown = this.spinner.countdown;
    if (!countdown) {
      return this.copy({ segments: [] });
    }
    const startsAt = countdown.startsAt.getTime();
    const endsAt = countdown.endsAt.getTime();
    const count = Math.max(Math.ceil((endsAt - startsAt) / SolSpinnerState.secondMs), 1);
    const left = Math.min(Math.max(Math.ceil((endsAt - this.now) / SolSpinnerState.secondMs), 0), count);
    const slot = SolSpinnerState.ringLength / count;
    const arc = slot - Math.min(SolSpinnerState.segmentGap, slot / SolSpinnerState.maxGapDivisor);
    const dash = `${arc} ${SolSpinnerState.ringLength - arc}`;
    const segments = Array.from({ length: count }, (_, id) => ({
      id,
      dash,
      offset: `${-(id * slot)}`,
      segmentClass: id < count - left ? 'sol-spinner-track' : 'sol-spinner-ink',
    }));
    return this.copy({ segments });
  }

  private copy(props: Partial<SolSpinnerState>): SolSpinnerState {
    return ObjectTools.copy<SolSpinnerState>(this, props);
  }
}
